import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, ExternalLink, Loader2, Package } from "lucide-react";
import type { AccountPackagesSummary } from "@wedding/shared";
import { organizerPackagesApi } from "../lib/api";
import { paths } from "../lib/paths";
import { AppNavLink } from "../lib/route-primitives";

const WEB_BASE = (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

/**
 * Profil → "Hesap ve paketler" (paket modeli, 2026-10-16). Her düğün bir pakete bağlıdır:
 * hesabın 1 Free paketi + her ödenmiş sipariş bir paket. Burada yalnız özet (Free paketin
 * durumu + kullanılabilir / kullanımda / ödeme bekleyen); paketler ve sipariş geçmişi Paketlerim'de.
 * Hesaba özel plan (eski Pro/Max hesap, paket modeline henüz taşınmamış) ayrıca açıklanır.
 */
export function AccountPackages({ accountLabel, accountWide = false }: { accountLabel: string; accountWide?: boolean }) {
  const { t } = useTranslation();
  const [pk, setPk] = useState<AccountPackagesSummary | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    organizerPackagesApi
      .list()
      .then((res) => {
        if (!alive) return;
        if (res.data) setPk(res.data.packages);
        else setFailed(true);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  const free = pk ? [...pk.available, ...pk.inUse, ...pk.ended].find((p) => p.source === "free") ?? null : null;
  const walletList = pk
    ? Object.entries(pk.available.reduce<Record<string, number>>((acc, p) => ({ ...acc, [p.label]: (acc[p.label] ?? 0) + 1 }), {}))
        .map(([label, n]) => `${n} ${label}`)
        .join(" · ")
    : "";

  return (
    <div className="p-5 space-y-4" data-testid="account-packages">
      {accountWide ? (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("profile.accountWideTitle")}</p>
          <p className="text-sm font-semibold">{t("profile.accountWideBadge", { plan: accountLabel })}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{t("profile.accountWideExplain", { plan: accountLabel })}</p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground leading-relaxed">{t("profile.packagesExplain")}</p>
      )}

      {failed ? (
        <p className="text-xs text-red-500">{t("packages.loadFailed")}</p>
      ) : !pk ? (
        <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      ) : (
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="rounded-xl border border-border p-3" data-testid="account-free-package">
            <dt className="text-xs text-muted-foreground uppercase tracking-wider">{t("profile.freeTitle")}</dt>
            <dd className="mt-1 font-medium">
              {!free
                ? t("profile.freeStatus.used")
                : free.status === "available"
                  ? t("profile.freeStatus.available")
                  : free.status === "assigned"
                    ? t("profile.freeStatus.assigned", { title: free.wedding?.title ?? "" })
                    : t("profile.freeStatus.ended")}
            </dd>
          </div>
          <div className="rounded-xl border border-border p-3" data-testid="account-wallet">
            <dt className="text-xs text-muted-foreground uppercase tracking-wider">{t("wedding.walletAvailable")}</dt>
            <dd className="mt-1 font-medium">{walletList || t("wedding.walletNone")}</dd>
            <dd className="mt-0.5 text-xs text-muted-foreground">
              {t("wedding.walletInUse", { n: pk.inUse.length })}
              {pk.pending.length > 0 && ` · ${t("wedding.walletPending", { n: pk.pending.length })}`}
            </dd>
          </div>
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <AppNavLink
          to={paths.packages}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline cursor-pointer"
        >
          <Package className="h-3.5 w-3.5" aria-hidden="true" />
          <span>{t("profile.packagesLink")}</span>
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </AppNavLink>
        <a
          href={`${WEB_BASE}/#plans`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline cursor-pointer"
        >
          <span>{t("profile.buyPackage")}</span>
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      </div>
    </div>
  );
}
